import { lazy, Suspense, useEffect, useState } from 'react'
import '@shared/contact-form/rivisto.css'
import { useCopy, useLocale } from '../../content'
import { useSede } from '../../sede'
import { Eyebrow } from '../layout/Eyebrow'
import { FormErrorBoundary } from './FormErrorBoundary'
import { formContactInfo } from './formContactInfo'

const ContactForm = lazy(() => import('@shared/contact-form').then((m) => ({ default: m.ContactForm })))

/**
 * "Contattaci": forest band with the shared AVMG richieste form.
 *
 * Client-only: the wizard is interactive and has no SEO value, so the
 * prerender ships a same-height skeleton and the real form mounts after
 * hydration. The server render and the first client render are identical
 * (`mounted` is false in both); only the effect flips it, so hydration
 * never sees the form. The skeleton doubles as the Suspense fallback while
 * the form's chunk loads, so the band keeps its height throughout. If the
 * chunk fails to load, `FormErrorBoundary` swaps in a mailto line instead
 * of letting the error unmount the page.
 *
 * Band text uses `on-forest` at ≥ 95 % (spec §5): `paper` turns pine in
 * dark mode and would read ~2.5–3.9:1 on the lifted forest.
 *
 * The form renders its «rivisto» look (Richieste Lab, 2026-09-24): the
 * cream + dark-green palette comes from the --rf-* variables that
 * styles/vc2.css sets on .vc2-root for light and dark.
 */
export function ContactSection() {
  const copy = useCopy()
  const locale = useLocale()
  const sede = useSede()
  const { contactInfo, hideInfoPanel } = formContactInfo(sede, locale)
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  const skeleton = (
    <div aria-hidden="true" className="min-h-[760px] rounded-2xl border border-white/15 bg-white/10 animate-pulse motion-reduce:animate-none" />
  )
  return (
    <section id="contatti" className="bg-vc2-forest text-vc2-on-forest">
      <div className="vc2-container py-20 md:py-28">
        <div className="mx-auto max-w-2xl text-center">
          <Eyebrow className="text-center text-vc2-on-forest/95">{copy.contact.eyebrow}</Eyebrow>
          <h2 className="vc2-display mt-4 text-4xl md:text-5xl">{copy.contact.title}</h2>
          <p className="mt-4 text-vc2-on-forest/95">{copy.contact.intro}</p>
          <p className="mt-2 text-sm text-vc2-on-forest/95">{copy.contact.note}</p>
        </div>
        <div className="mt-10">
          {mounted ? (
            <FormErrorBoundary message={copy.contact.formUnavailable} email={sede.contact.email}>
              <Suspense fallback={skeleton}>
                <ContactForm
                  look="rivisto"
                  contactInfo={contactInfo}
                  extraPayload={{ sede_id: sede.sedeId, lang: locale }}
                  hideInfoPanel={hideInfoPanel}
                  hideInfoHeading
                  formality="lei"
                  askClientType
                  strings={copy.contact.form}
                />
              </Suspense>
            </FormErrorBoundary>
          ) : (
            skeleton
          )}
        </div>
      </div>
    </section>
  )
}
